import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ModerationFlagsList } from "./ModerationFlagsList";
import { ConfigModal } from "./ConfigModal";
import { useModerationStore } from "../../store/moderationStore";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useDialogState } from "../../hooks/useDialogState";

interface ModerationManagementProps {
  onBack?: () => void;
}

export const Management: React.FC<ModerationManagementProps> = ({ onBack }) => {
  const configDialog = useDialogState();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { loadFlags } = useModerationStore();

  const defaultOnBack = () => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem("activeScreenId", "videoPlatform");
      window.location.reload();
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadFlags()
      .then(() => {
        showSnackbar("Moderation flags refreshed successfully", "success");
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to refresh";
        showSnackbar(errorMessage, "error");
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const handleOpenConfig = () => {
    configDialog.openDialog();
  };

  const handleCloseConfig = () => {
    configDialog.close();
  };

  const handleConfigSubmit = () => {
    handleRefresh();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Chat Moderation
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage chat moderation flags and configuration
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
          <IconButton
            onClick={onBack || defaultOnBack}
            color="primary"
            sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
            title="Back to Video Platform"
          >
            <ArrowBackIcon />
          </IconButton>

          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            color="primary"
            sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>

          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleOpenConfig}
            sx={{
              backgroundColor: "#7357FF",
              "&:hover": { backgroundColor: "#5a42cc" },
            }}
          >
            Configuration
          </Button>
        </Box>

        <ModerationFlagsList onRefresh={handleRefresh} />

        <ConfigModal
          open={configDialog.open}
          onClose={handleCloseConfig}
          onSubmit={handleConfigSubmit}
        />

        {SnackbarNode}
      </Box>
    </Box>
  );
};
